import { useState, useCallback } from 'react'
import {
  Stack,
  Dropdown,
  IDropdownOption,
  DatePicker,
  IColumn,
  IconButton,
  MessageBar,
  MessageBarType,
  Text,
  Panel,
  PanelType,
} from '@fluentui/react'
import { apiService } from '../services/apiService'
import { useApi } from '../hooks/useApi'
import { useTheme } from '../context/ThemeContext'
import { DataTable } from '../components/DataTable'
import type { AuditLogEntry, PagedResponse } from '../types'

export function AuditLog() {
  const { isDarkMode } = useTheme()
  const [entityType, setEntityType] = useState<string>('')
  const [action, setAction] = useState<string>('')
  const [startDate, setStartDate] = useState<Date | undefined>()
  const [endDate, setEndDate] = useState<Date | undefined>()
  const [page, setPage] = useState(1)
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null)
  const pageSize = 50

  const fetchAuditLog = useCallback(
    () => apiService.getAuditLog({
      page,
      pageSize,
      entityType: entityType || undefined,
      action: action || undefined,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
    }),
    [page, pageSize, entityType, action, startDate, endDate]
  )

  const {
    data: auditLog,
    loading,
    error,
  } = useApi<PagedResponse<AuditLogEntry>>(fetchAuditLog, [page, entityType, action, startDate, endDate])

  const entityTypeOptions: IDropdownOption[] = [
    { key: '', text: 'All Entity Types' },
    { key: 'Application', text: 'Application' },
    { key: 'Credential', text: 'Credential' },
    { key: 'Owner', text: 'Owner' },
    { key: 'BusinessUnit', text: 'Business Unit' },
  ]

  const actionOptions: IDropdownOption[] = [
    { key: '', text: 'All Actions' },
    { key: 'Create', text: 'Create' },
    { key: 'Update', text: 'Update' },
    { key: 'Delete', text: 'Delete' },
    { key: 'Renewed', text: 'Renewed' },
    { key: 'Sync', text: 'Sync' },
  ]

  const getActionColor = (actionName: string): string => {
    const upper = actionName.toUpperCase()
    if (upper.includes('DELETE') || upper.includes('DEPART') || upper.includes('REMOVE')) return '#a4262c'
    if (upper.includes('CREATE') || upper.includes('ADD')) return '#107c10'
    if (upper.includes('RENEW')) return '#8764b8'
    if (upper.includes('SYNC') || upper.includes('UPDATE')) return '#0078d4'
    return '#605e5c'
  }

  const formatActionLabel = (actionName: string): string =>
    actionName
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase())

  const parseUtcTimestamp = (timestamp: string): Date => {
    // Ensure UTC timestamps from the server are parsed correctly
    // If no timezone indicator, append 'Z' to treat as UTC
    if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
      return new Date(timestamp + 'Z')
    }
    return new Date(timestamp)
  }

  const columns: IColumn[] = [
    {
      key: 'timestamp',
      name: 'Date/Time',
      minWidth: 160,
      isResizable: true,
      onRender: (item: AuditLogEntry) => {
        const dateStr = parseUtcTimestamp(item.timestamp).toLocaleString()
        return <Text variant="small" title={dateStr}>{dateStr}</Text>
      },
    },
    {
      key: 'action',
      name: 'Action',
      minWidth: 100,
      isResizable: true,
      onRender: (item: AuditLogEntry) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <span
            title={item.action}
            style={{
              padding: '3px 10px',
              borderRadius: 12,
              fontSize: 11,
              fontWeight: 600,
              backgroundColor: `${getActionColor(item.action)}18`,
              color: getActionColor(item.action),
              maxWidth: 140,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              textAlign: 'center',
            }}
          >
            {formatActionLabel(item.action)}
          </span>
        </div>
      ),
    },
    {
      key: 'entityType',
      name: 'Entity Type',
      fieldName: 'entityType',
      minWidth: 110,
      isResizable: true,
    },
    {
      key: 'entityName',
      name: 'Entity',
      minWidth: 300,
      isResizable: true,
      onRender: (item: AuditLogEntry) => {
        // Show entity name (which includes Entra App ID for Azure reference)
        // Fall back to generic text if not available
        const name = item.entityName || 'N/A'
        return <span title={name}>{name}</span>
      },
    },
    {
      key: 'user',
      name: 'User',
      minWidth: 180,
      isResizable: true,
      onRender: (item: AuditLogEntry) => {
        const user = item.userName || item.userId || 'System'
        return <span title={user}>{user}</span>
      },
    },
    {
      key: 'changes',
      name: 'Changes',
      minWidth: 70,
      isResizable: true,
      onRender: (item: AuditLogEntry) => {
        if (!item.oldValues && !item.newValues) {
          return <span style={{ color: '#605e5c' }}>-</span>
        }
        return (
          <span
            style={{ color: '#0078d4', cursor: 'pointer' }}
            onClick={() => setSelectedEntry(item)}
          >
            View
          </span>
        )
      },
    },
    {
      key: 'actions',
      name: '',
      minWidth: 40,
      maxWidth: 40,
      isResizable: false,
      onRender: (item: AuditLogEntry) => (
        <IconButton
          iconProps={{ iconName: 'Info' }}
          title="View Details"
          onClick={() => setSelectedEntry(item)}
        />
      ),
    },
  ]

  const formatJsonForDisplay = (json?: string): React.ReactNode => {
    if (!json) return <span style={{ color: '#605e5c' }}>None</span>
    try {
      const parsed = JSON.parse(json)
      return (
        <pre
          style={{
            backgroundColor: isDarkMode ? '#1a1a2e' : '#f3f2f1',
            color: isDarkMode ? '#d4d4d4' : '#323130',
            border: `1px solid ${isDarkMode ? '#333' : '#edebe9'}`,
            padding: 12,
            borderRadius: 4,
            overflow: 'auto',
            fontSize: 12,
            maxHeight: 300,
          }}
        >
          {JSON.stringify(parsed, null, 2)}
        </pre>
      )
    } catch {
      return <span>{json}</span>
    }
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Audit Log</h1>
        <p className="page-description">
          Track all changes made to applications, credentials, and configuration
        </p>
      </div>

      {/* Filters */}
      <div className="card">
        <Stack horizontal tokens={{ childrenGap: 16 }} wrap styles={{ root: { alignItems: 'center', paddingBottom: 16, overflow: 'visible' } }}>
          <Dropdown
            label="Entity Type"
            options={entityTypeOptions}
            selectedKey={entityType}
            onChange={(_, option) => {
              setEntityType(option?.key?.toString() || '')
              setPage(1)
            }}
            styles={{ root: { width: 160 } }}
          />
          <Dropdown
            label="Action"
            options={actionOptions}
            selectedKey={action}
            onChange={(_, option) => {
              setAction(option?.key?.toString() || '')
              setPage(1)
            }}
            styles={{ root: { width: 140 } }}
          />
          <DatePicker
            label="Start Date"
            value={startDate}
            onSelectDate={(date) => {
              setStartDate(date || undefined)
              setPage(1)
            }}
            placeholder="Select start date"
            styles={{ root: { width: 160, marginTop: 10 } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onSelectDate={(date) => {
              setEndDate(date || undefined)
              setPage(1)
            }}
            placeholder="Select end date"
            styles={{ root: { width: 160, marginTop: 10 } }}
          />
          {(entityType || action || startDate || endDate) && (
            <span
              onClick={() => {
                setEntityType('')
                setAction('')
                setStartDate(undefined)
                setEndDate(undefined)
                setPage(1)
              }}
              style={{
                marginTop: 46,
                fontSize: 13,
                color: '#0078d4',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              Clear filters
            </span>
          )}
        </Stack>
      </div>

      {/* Results */}
      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          styles={{ root: { marginBottom: 16 } }}
        >
          {error}
        </MessageBar>
      )}

      <div className="card">
        <DataTable
          items={auditLog?.items || []}
          columns={columns}
          loading={loading}
          emptyMessage="No audit log entries found"
        />

        {/* Pagination */}
        {auditLog && auditLog.totalPages > 1 && (
          <Stack
            horizontal
            horizontalAlign="center"
            tokens={{ childrenGap: 8 }}
            styles={{ root: { marginTop: 16 } }}
          >
            <IconButton
              iconProps={{ iconName: 'ChevronLeft' }}
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            />
            <span style={{ padding: '8px 16px' }}>
              Page {page} of {auditLog.totalPages}
            </span>
            <IconButton
              iconProps={{ iconName: 'ChevronRight' }}
              disabled={page === auditLog.totalPages}
              onClick={() => setPage(p => p + 1)}
            />
          </Stack>
        )}
      </div>

      {/* Detail Panel */}
      {selectedEntry && (
        <Panel
          isOpen={true}
          onDismiss={() => setSelectedEntry(null)}
          headerText="Audit Log Entry Details"
          type={PanelType.medium}
          isLightDismiss
        >
          <Stack tokens={{ childrenGap: 20 }} styles={{ root: { marginTop: 20 } }}>
            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c', display: 'block', marginBottom: 4 } }}>
                Date/Time
              </Text>
              <Text>{parseUtcTimestamp(selectedEntry.timestamp).toLocaleString()}</Text>
            </div>

            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c', display: 'block', marginBottom: 4 } }}>
                Action
              </Text>
              <span
                style={{
                  padding: '4px 10px',
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 500,
                  backgroundColor: `${getActionColor(selectedEntry.action)}15`,
                  color: getActionColor(selectedEntry.action),
                }}
              >
                {selectedEntry.action}
              </span>
            </div>

            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c', display: 'block', marginBottom: 4 } }}>
                Entity
              </Text>
              <Text>
                {selectedEntry.entityType}: {selectedEntry.entityName || `ID ${selectedEntry.entityId}`}
                {selectedEntry.entityName && (
                  <span style={{ color: '#605e5c', fontSize: 12 }}> (ID: {selectedEntry.entityId})</span>
                )}
              </Text>
            </div>

            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c', display: 'block', marginBottom: 4 } }}>
                User
              </Text>
              <Text>{selectedEntry.userName || selectedEntry.userId || 'System'}</Text>
            </div>

            {selectedEntry.ipAddress && (
              <div>
                <Text variant="small" styles={{ root: { color: '#605e5c', display: 'block', marginBottom: 4 } }}>
                  IP Address
                </Text>
                <Text styles={{ root: { fontFamily: 'monospace' } }}>{selectedEntry.ipAddress}</Text>
              </div>
            )}

            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c', display: 'block', marginBottom: 8 } }}>
                Previous Values
              </Text>
              {formatJsonForDisplay(selectedEntry.oldValues)}
            </div>

            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c', display: 'block', marginBottom: 8 } }}>
                New Values
              </Text>
              {formatJsonForDisplay(selectedEntry.newValues)}
            </div>
          </Stack>
        </Panel>
      )}
    </div>
  )
}
